package com.evoapps.evoshift.domain.usecase.marketplace

import com.evoapps.evoshift.domain.model.AvailabilityStatus
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.workerId
import com.evoapps.evoshift.domain.repository.ShiftRepository

/** SH-165: what the swap picker says about a colleague on the shift's day. */
enum class SwapCandidateStatus {
    /** Said they are available that day and do not work then. */
    AVAILABLE,
    /** Has not sent the month (or it could not be read). */
    NO_ANSWER,
    /** Said they are not available that day. */
    UNAVAILABLE,
    /** Already has a shift that day at this workplace. */
    WORKING
}

data class SwapCandidate(val user: User, val status: SwapCandidateStatus)

/**
 * SH-165: the colleagues to offer in "Swap with someone", best first: available that day and not
 * working, then those who have not answered, then the unavailable, then those already working that
 * day; by name within each. Only active colleagues who joined (not invited shadow profiles), and
 * where the shift needs a role (or skills), only those with it. Availability comes from the server
 * ([ShiftRepository.swapAvailability]); without it everyone not working reads as "no answer".
 */
class GetSwapCandidatesUseCase(private val shiftRepository: ShiftRepository) {

    suspend operator fun invoke(shift: Shift, colleagues: List<User>, rosterShifts: List<Shift>): List<SwapCandidate> {
        val availability = shiftRepository.swapAvailability(shift.id).getOrNull().orEmpty()
        return rank(shift, colleagues, rosterShifts, availability)
    }

    companion object {
        /** The ordering itself, from what is known (pure). */
        fun rank(
            shift: Shift,
            colleagues: List<User>,
            rosterShifts: List<Shift>,
            availability: Map<String, AvailabilityStatus>
        ): List<SwapCandidate> {
            val workplaceId = RosterIds.workplaceIdOf(shift.rosterId)
            val working = rosterShifts.filter { it.date == shift.date && it.id != shift.id }.mapNotNull { it.workerId }.toSet()
            return colleagues
                .filter { user ->
                    val engagement = user.engagements[workplaceId] ?: return@filter false
                    user.uid != shift.userId && !user.isShadow && engagement.isActive &&
                        (shift.requiredRole.isNullOrBlank() || engagement.role == shift.requiredRole) &&
                        shift.requiredSkills.all { it in engagement.skills }
                }
                .map { user ->
                    val status = when {
                        user.uid in working -> SwapCandidateStatus.WORKING
                        else -> when (availability[user.uid]) {
                            AvailabilityStatus.AVAILABLE -> SwapCandidateStatus.AVAILABLE
                            AvailabilityStatus.UNAVAILABLE -> SwapCandidateStatus.UNAVAILABLE
                            null -> SwapCandidateStatus.NO_ANSWER
                        }
                    }
                    SwapCandidate(user, status)
                }
                .sortedWith(compareBy<SwapCandidate> { it.status.ordinal }.thenBy { it.user.displayName.lowercase() })
        }
    }
}
